import { ApiError } from '@api/api-client';
import { createEdition } from '@api/editions-api';
import { listSites, type SiteResponse } from '@api/sites-api';
import ConfirmationModal from '@components/confirmation-modal';
import CountryPicker from '@components/country-picker';
import FieldError from '@components/field-error';
import Modal from '@components/modal';
import YearPicker from '@components/year-picker';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { type EditionFormData, editionSchema } from '@validation/schemas';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { sileo } from 'sileo';

interface CreateEditionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateEditionModal({
  isOpen,
  onClose,
}: CreateEditionModalProps): React.JSX.Element {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
    watch,
    control,
  } = useForm<EditionFormData>({
    resolver: zodResolver(editionSchema),
    defaultValues: {
      name: '',
      year: undefined as unknown as number,
      countryId: '',
      siteId: '',
      externalId: undefined,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        name: '',
        year: undefined as unknown as number,
        countryId: '',
        siteId: '',
        externalId: undefined,
      });
    }
  }, [isOpen, reset]);

  const countryId = watch('countryId');

  const { data: sites = [] } = useQuery({
    queryKey: ['sites', countryId],
    queryFn: () => listSites(countryId),
    enabled: Boolean(countryId),
    staleTime: 60_000,
  });

  // Only PMS sites are a choice here: a marketplace edition resolves to the
  // country's SSO site on the backend, so it needs no explicit pick.
  const pmsSites = sites.filter((s: SiteResponse) => s.authMode === 'password');

  // A site chosen for one country must not linger when the country changes.
  useEffect(() => {
    setValue('siteId', '');
  }, [countryId, setValue]);

  const onValid = (): void => {
    setIsConfirmOpen(true);
  };

  const handleConfirm = async (): Promise<void> => {
    const data = getValues();

    try {
      await createEdition({
        countryId: data.countryId,
        ...(data.siteId ? { siteId: data.siteId } : {}),
        name: data.name.trim(),
        year: data.year,
        externalId: data.externalId,
      });
    } catch (error) {
      // Keep the form open with its values so the CSV id (or whatever clashed) can
      // be fixed, instead of the confirm silently doing nothing.
      sileo.error({
        title: error instanceof ApiError ? error.message : 'Could not create the edition',
      });
      setIsConfirmOpen(false);
      return;
    }

    sileo.success({ title: 'Edition created successfully' });
    setIsConfirmOpen(false);
    handleClose();
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="New edition"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleSubmit(onValid)()}
            >
              <Save size={16} />
              Create edition
            </button>
          </>
        }
      >
        <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label htmlFor="edition-name" className="form-label">
              Name
            </label>
            <input
              id="edition-name"
              type="text"
              className={`form-input${errors.name ? ' form-input--error' : ''}`}
              placeholder="e.g. DP Awards Spain 2026"
              {...register('name')}
            />
            <FieldError error={errors.name} />
          </div>

          <div className="form-group">
            <label htmlFor="edition-external-id" className="form-label">
              CSV ID
            </label>
            <input
              id="edition-external-id"
              type="number"
              className={`form-input${errors.externalId ? ' form-input--error' : ''}`}
              placeholder="Optional"
              {...register('externalId', {
                setValueAs: (v: string) => (v === '' ? undefined : Number(v)),
              })}
            />
            <FieldError error={errors.externalId} />
          </div>

          <div className="form-group">
            <span className="form-label">Year</span>
            <Controller
              name="year"
              control={control}
              render={({ field }) => (
                <YearPicker value={field.value || null} onChange={field.onChange} />
              )}
            />
            <FieldError error={errors.year} />
          </div>

          <div className="form-group">
            <span className="form-label">Country</span>
            <Controller
              name="countryId"
              control={control}
              render={({ field }) => (
                <CountryPicker value={field.value} onChange={field.onChange} />
              )}
            />
            <FieldError error={errors.countryId} />
          </div>

          {/*
            Only shown once a country is picked and only if it has PMS sites: for a
            plain marketplace country there is nothing to choose, and the edition
            defaults to the SSO site.
          */}
          {pmsSites.length > 0 && (
            <div className="form-group">
              <label htmlFor="edition-site" className="form-label">
                Edition type
              </label>
              <Controller
                name="siteId"
                control={control}
                render={({ field }) => (
                  <select
                    id="edition-site"
                    className="form-select"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  >
                    <option value="">Marketplace (Doctoralia / MioDottore)</option>
                    {pmsSites.map((site) => (
                      <option key={site.id} value={site.id}>
                        PMS — {site.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              <p className="form-hint">
                A PMS edition has its own voters, imported and signing in with a password.
              </p>
            </div>
          )}
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => void handleConfirm()}
        title="Create edition"
        message="Are you sure you want to create this edition?"
        confirmLabel="Create"
      />
    </>
  );
}
